// Package imports:
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
// Component imports:
import Icon from '../../Header/CustomIcon/CustomIcon';
// Service imports:
import useComponentVisible from '../../../services/hooks';
// Type imports:
import { ICompanyViewModel } from '../../../types/CompanyTypes';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';

const KYCNavBar: React.FC<ICompanyViewModel> = () => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible();
    // State to track current URL path (pathname + hash)
    const [currentPath, setCurrentPath] = useState('');

    /**
     * Updates the current path state when the component mounts or URL changes
     * This combines the pathname and hash to get the full URL path
     */
    useEffect(() => {
        // Function to update the current path from window.location
        const updatePath = () => {
            setCurrentPath(`${window.location.pathname}${window.location.hash}`);
        };

        // Set initial path on component mount
        updatePath();

        // Listen for URL changes (when user navigates using browser back/forward buttons)
        window.addEventListener('popstate', updatePath);

        // Listen for hash changes (when hash part of URL changes)
        window.addEventListener('hashchange', updatePath);

        // Cleanup: remove event listeners when component unmounts
        return () => {
            window.removeEventListener('popstate', updatePath);
            window.removeEventListener('hashchange', updatePath);
        };
    }, []);

    /**
     * Checks if the current URL matches a given path pattern
     * @param path - The path to match against (can include hash)
     * @returns boolean - true if the current URL starts with the given path
     */
    const isActive = (path: string): boolean => {
        return currentPath.startsWith(path);
    };
    const isActiveMoreLoosely = (path: string): boolean => {
        return currentPath.includes(path);
    }

    return (
        <div className="KCL_sub-navbar">
            {/* Mobile menu version */}
            <ul className="mobileMenu">
                <li>
                    <span
                        className='list-category'
                        id="mobile"
                        onClick={() => setIsComponentVisible(!isComponentVisible)}
                    >
                        Áreiðanleikakannanir (KYC) <Icon type='dropdown-arrow' />
                    </span>
                    <ul ref={ref} className={cx("mobile dropdown", { "focused": isComponentVisible })}>
                        {/* Main KYC overview link - active when on /Areidanleikakannanir/Yfirlit */}
                        <li>
                            <a
                                href="/Areidanleikakannanir/Yfirlit"
                                className={cx({
                                    'active': isActive('/Areidanleikakannanir/Yfirlit') || isActiveMoreLoosely('/Areidanleikakannanir/Skoda-Svor')
                                })}
                            >
                                Áreiðanleikakannanir(KYC)
                            </a>
                        </li>

                        {/* AML monitoring section */}
                        <li className='list'>
                            <span>AML vöktun</span>
                            <ul className='list-items'>
                                {/* AML vaktin link - active when on /Valistar/Leit#AML-vaktin */}
                                <li>
                                    <a
                                        href="/Valistar/Leit#AML-vaktin"
                                        className={cx({ 'active': isActive('/Valistar/Leit#AML-vaktin') })}
                                    >
                                        AML vaktin
                                    </a>
                                </li>
                                {/* Disabled link - coming soon */}
                                <li className='disabled'>
                                    <span className="disabled">Atburðaskrá / tilkynningar</span>
                                </li>
                            </ul>
                        </li>

                        {/* Political connections link - active when on /Valistar/Leit#Island */}
                        <li>
                            <a
                                href="/Valistar/Leit#Island"
                                className={cx({ 'active': isActive('/Valistar/Leit#Island') })}
                            >
                                Stjórnmálaleg tengsl
                            </a>
                        </li>

                        {/* International watchlists section */}
                        <li className='list'>
                            <span>Alþjóðlegir válistar</span>
                            <ul className='list-items'>
                                {/* International watchlist for individuals */}
                                <li>
                                    <a
                                        href="/Valistar/Leit#Althjodlegt-Einstaklingar"
                                        className={cx({ 'active': isActive('/Valistar/Leit#Althjodlegt-Einstaklingar') })}
                                    >
                                        Alþjóðlegir válistar einstaklingar
                                    </a>
                                </li>
                                {/* International watchlist for legal entities */}
                                <li>
                                    <a
                                        href="/Valistar/Leit#Althjodlegt-Logadilar"
                                        className={cx({ 'active': isActive('/Valistar/Leit#Althjodlegt-Logadilar') })}
                                    >
                                        Alþjóðlegir válistar lögaðilar
                                    </a>
                                </li>
                            </ul>
                        </li>

                        {/* Guidelines link - active when on /Areidanleikakannanir/Leidbeiningar */}
                        <li>
                            <a
                                href="/Areidanleikakannanir/Leidbeiningar"
                                className={cx({ 'active': isActive('/Areidanleikakannanir/Leidbeiningar') })}
                            >
                                Leiðbeiningar
                            </a>
                        </li>
                    </ul>
                </li>
            </ul >

            {/* Desktop menu version */}
            < ul className='itemList' >
                {/* Main KYC overview link */}
                < li >
                    <a
                        className={cx('list-category', { 'active': isActive('/Areidanleikakannanir/Yfirlit') || isActiveMoreLoosely('/Areidanleikakannanir/Skoda-Svor') })}
                        href="/Areidanleikakannanir/Yfirlit"
                    >
                        Áreiðanleikakannanir (KYC)
                    </a>
                </li >

                {/* AML monitoring dropdown */}
                < li >
                    <span
                        className={cx('list-category', {
                            'active': isActive('/Valistar/Leit#AML-vaktin')
                        })}
                    >
                        AML vöktun <span className='icon'><Icon type='dropdown-arrow' /></span>
                    </span>
                    <ul className="dropdown">
                        <li>
                            <a
                                href="/Valistar/Leit#AML-vaktin"
                                className={cx({ 'active': isActive('/Valistar/Leit#AML-vaktin') })}
                            >
                                AML vaktin
                            </a>
                        </li>
                        {/* Disabled coming soon link */}
                        <li className='disabled'>
                            <Tooltip
                                noUnderline
                                text={<span className='disabled'>Atburðaskrá / tilkynningar</span>}
                                tooltip={"Væntanlegt"}
                            />
                        </li>
                    </ul>
                </li >

                {/* Political connections link */}
                < li >
                    <a
                        className={cx('list-category', { 'active': isActive('/Valistar/Leit#Island') })}
                        href="/Valistar/Leit#Island"
                    >
                        Stjórnmálaleg tengsl
                    </a>
                </li >

                {/* International watchlists dropdown */}
                < li >
                    <span
                        className={cx('list-category', {
                            'active': isActive('/Valistar/Leit#Althjodlegt-Einstaklingar') ||
                                isActive('/Valistar/Leit#Althjodlegt-Logadilar')
                        })}
                    >
                        Alþjóðlegir válistar <span className='icon'><Icon type='dropdown-arrow' /></span>
                    </span>
                    <ul className="dropdown">
                        <li>
                            <a
                                href="/Valistar/Leit#Althjodlegt-Einstaklingar"
                                className={cx({ 'active': isActive('/Valistar/Leit#Althjodlegt-Einstaklingar') })}
                            >
                                Alþjóðlegir válistar einstaklingar
                            </a>
                        </li>
                        <li>
                            <a
                                href="/Valistar/Leit#Althjodlegt-Logadilar"
                                className={cx({ 'active': isActive('/Valistar/Leit#Althjodlegt-Logadilar') })}
                            >
                                Alþjóðlegir válistar lögaðilar
                            </a>
                        </li>
                    </ul>
                </li >

                {/* Guidelines link */}
                < li >
                    <a
                        className={cx('list-category', { 'active': isActive('/Areidanleikakannanir/Leidbeiningar') })}
                        href="/Areidanleikakannanir/Leidbeiningar"
                    >
                        Leiðbeiningar
                    </a>
                </li >
            </ul >
        </div >
    );
};

export default KYCNavBar;